import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const HomeServices = () => {
  const featuredServices = [
    {
      title: "Liver Transplantation",
      description: "State-of-the-art liver transplant procedures with comprehensive pre and post-operative care.",
      icon: "🫁"
    },
    {
      title: "Hepatic Oncology",
      description: "Advanced treatment for liver cancer using cutting-edge surgical techniques and therapies.",
      icon: "🏥"
    },
    {
      title: "Digestive Care",
      description: "Expert care for various digestive system disorders with personalized treatment plans.",
      icon: "🔬"
    }
  ];

  return (
    <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-sage/10 to-softwhite relative">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.05, scale: 1 }}
          transition={{ duration: 1 }}
          className="absolute -right-64 -top-64 w-[500px] h-[500px] rounded-full bg-golden-yellow"
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-darkbrown mb-4">
            Comprehensive Liver Care Services
          </h2>
          <p className="text-earthybeige text-lg">
            Offering advanced treatments and personalized care plans for all liver-related conditions
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {featuredServices.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              whileHover={{ y: -5 }}
              className="bg-softwhite rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
            >
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold text-darkbrown mb-3">
                {service.title}
              </h3>
              <p className="text-earthybeige mb-4">
                {service.description}
              </p>
              <motion.button
                whileHover={{ x: 5 }}
                className="flex items-center text-golden-yellow hover:text-palegold transition-colors"
              >
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-16 text-center"
        >
          {[
            { value: "500+", label: "Successful Surgeries" },
            { value: "15+", label: "Years Experience" },
            { value: "24/7", label: "Emergency Care" }
          ].map((stat, index) => (
            <div key={index} className="space-y-2">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: 0.1 * index }}
                className="text-3xl font-bold text-golden-yellow"
              >
                {stat.value}
              </motion.div>
              <p className="text-brown">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* view all services button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center mt-16"
        >
          <Link to="/services">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-golden-yellow hover:bg-charcol text-white px-8 py-3 rounded-lg font-semibold flex items-center transition-colors"
            >
              View All Services
              <ChevronRight className="w-5 h-5 ml-2" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeServices;